import React from 'react';
import { Box, Skeleton, Stack, Paper } from '@mui/material';

interface LoadingSkeletonProps {
  variant?: 'news-card' | 'featured-news' | 'article' | 'list' | 'text';
  count?: number;
  height?: number | string;
  width?: number | string;
}

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({ 
  variant = 'text', 
  count = 1, 
  height = 200, 
  width = '100%' 
}) => {
  const renderNewsCardSkeleton = () => (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Skeleton variant="rectangular" height={200} animation="wave" />
      <Box sx={{ p: 2 }}>
        <Skeleton variant="text" height={24} width="80%" sx={{ mb: 1 }} animation="wave" />
        <Skeleton variant="text" height={20} width="100%" sx={{ mb: 1 }} animation="wave" />
        <Skeleton variant="text" height={20} width="60%" sx={{ mb: 2 }} animation="wave" />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Skeleton variant="text" width={80} height={16} animation="wave" />
          <Skeleton variant="text" width={100} height={16} animation="wave" />
        </Box>
      </Box>
    </Paper>
  );

  const renderFeaturedNewsSkeleton = () => (
    <Box sx={{ mb: 6 }}>
      <Skeleton variant="text" height={40} width="300px" sx={{ mb: 3 }} animation="wave" />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Box sx={{ flex: 3 }}>
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Skeleton variant="rectangular" height={400} animation="wave" />
            <Box sx={{ p: 3 }}>
              <Skeleton variant="text" height={32} width="90%" sx={{ mb: 2 }} animation="wave" />
              <Skeleton variant="text" height={20} width="100%" sx={{ mb: 1 }} animation="wave" />
              <Skeleton variant="text" height={20} width="70%" animation="wave" />
            </Box>
          </Paper>
        </Box>
        <Box sx={{ flex: 2, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 2 }}>
          {[1, 2].map((item) => (
            <Paper key={item} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Skeleton variant="rectangular" height={150} animation="wave" />
              <Box sx={{ p: 2 }}>
                <Skeleton variant="text" height={20} width="90%" sx={{ mb: 1 }} animation="wave" />
                <Skeleton variant="text" height={16} width="60%" animation="wave" />
              </Box>
            </Paper>
          ))}
        </Box>
      </Stack>
    </Box>
  );

  const renderArticleSkeleton = () => (
    <Box>
      <Skeleton variant="text" height={24} width="200px" sx={{ mb: 2 }} animation="wave" />
      <Skeleton variant="text" height={48} width="90%" sx={{ mb: 3 }} animation="wave" />
      <Skeleton variant="rectangular" height={400} sx={{ mb: 4, borderRadius: 2 }} animation="wave" />
      {[1, 2, 3, 4].map((item) => (
        <Skeleton key={item} variant="text" height={20} sx={{ mb: 1 }} animation="wave" />
      ))}
      <Box sx={{ mt: 4 }}>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} animation="wave" />
      </Box>
    </Box>
  );

  const renderListSkeleton = () => (
    <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap">
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ width: { xs: '100%', sm: '47%', md: '31%', lg: '23%' } }}>
          {renderNewsCardSkeleton()}
        </Box>
      ))}
    </Stack>
  );

  const renderTextSkeleton = () => (
    <Skeleton variant="text" height={height} width={width} animation="wave" />
  );

  const renderSkeleton = () => {
    switch (variant) {
      case 'news-card':
        return renderNewsCardSkeleton();
      case 'featured-news':
        return renderFeaturedNewsSkeleton();
      case 'article':
        return renderArticleSkeleton();
      case 'list':
        return renderListSkeleton();
      case 'text':
      default:
        return renderTextSkeleton();
    }
  };

  if (variant === 'list' || variant === 'featured-news' || variant === 'article') {
    return <>{renderSkeleton()}</>;
  }

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ mb: variant === 'news-card' ? 3 : 1 }}>
          {renderSkeleton()}
        </Box>
      ))}
    </>
  );
};

export default LoadingSkeleton; 